import Link from "next/link";

import { getDefaultBuildYourOwnShopNumber } from "@stadium/redux-utils/selectors/configSelector";
import { useConfig } from "@stadium/hooks/lib/use-config";

import { ExtLink } from "@stadium/ui/src/components/link";

import Modal from "@stadium/ui/src/components/ModalNew";
import {
  CloseButton,
  ModalBody,
  ModalContent,
  ModalDialog,
  ModalFooter,
} from "@stadium/ui/src/components/ModalNew/utils";

import Button, { LinkButton } from "@sharedComponents/buttons";

import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";

import StorePreviewImage from "@sharedComponents/store-preview-image";

import { isEshopStore } from "@helpers/shop";
import { getCreateNewShopRoute } from "@stadium/helpers/app-routes";
import { getPreviewTemplateRoute } from "@constants/routes";

import {
  DEFAULT_PUBLIC_SHOP_LONG_DES,
  USER_SHOP_DATA,
} from "@constants/templates-listing/info-pop-up";

import styles from "./purge-main.module.scss";
import { useWorkspaceContext } from "@stadium/hooks/lib/workspace";

const emptyObj = {};

const TemplateInfoModal = ({ data = emptyObj, closeModal: closeTemplateInfoModal }) => {
  const { templateInfoData } = data;
  const {
    storeName,
    longDescription,
    tags,
    topBannerImage,
    sampleProductsImage,
    feUrl,
    storeNumber,
    onTemplateSelect,
    type = "",
  } = templateInfoData;
  const { uTitle, uDescription, uPrimaryCtaText, uSecondaryCtaText, uAnchorLink } = USER_SHOP_DATA;
  const buildYourOwnShopNumber = useConfig(getDefaultBuildYourOwnShopNumber);
  const { workspaceSlug } = useWorkspaceContext();
  const getShopDetails = () => {
    if (isEshopStore(type)) {
      return {
        title: uTitle,
        description: uDescription,
        ctaText: uPrimaryCtaText,
        shopNumber: buildYourOwnShopNumber,
        secondaryCtaText: uSecondaryCtaText,
        linkTo: uAnchorLink,
      };
    } else {
      return {
        title: storeName,
        description: longDescription || DEFAULT_PUBLIC_SHOP_LONG_DES,
        ctaText: "USE TEMPLATE",
        shopNumber: storeNumber,
      };
    }
  };

  const { title, ctaText, description, shopNumber, shopCode, linkTo, secondaryCtaText } =
    getShopDetails();

  const useTemplateUrl = getCreateNewShopRoute({ shopNumber, shopCode, workspaceSlug });

  const handleUseTemplateClick = () => {
    onTemplateSelect(useTemplateUrl);
  };

  const secondaryCtaLink = secondaryCtaText && (
    <ExtLink href={linkTo} target="_blank">
      {secondaryCtaText}
    </ExtLink>
  );

  const renderShopHeader = () => (
    <div className={styles.templateModalHeader}>
      <div className={styles.templateModalTitle}>{title}</div>
    </div>
  );

  const renderShopContent = () => (
    <div className={styles.templateModalDescriptionBlock}>
      <div className={styles.templateModalDescriptionPreviewText}>Shop Overview</div>
      <div className={styles.templateModalDescriptionTextRow}>
        {description}
        {secondaryCtaLink}
      </div>
      {tags?.length > 0 && (
        <div className={styles.templateModalDescriptionTagsBlock}>
          <div className={styles.templateModalDescriptionPreviewText}>Template Tags</div>
          <div className={styles.templateModalDescriptionTagsRow}>
            {tags.map((tag) => (
              <div key={tag} className={styles.templateModalDescriptionTagsHolder}>
                {tag}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderShopImage = () => (
    <Link href={getPreviewTemplateRoute(feUrl)}>
      <StorePreviewImage
        topBanner={topBannerImage}
        productsThumbImage={sampleProductsImage}
        isNavBar={true}
        storeName={storeName}
        width={554}
        height={443}
        className={styles.templateBanner}
        topBannerClassName={styles.templateBannerTopImage}
        bottomBannerClassName={styles.templateBannerBottomImage}
        navBarClassName={styles.templateNavBarBlock}
        logoClassName={styles.templateLogoText}
        parentClassName={styles.imageBanner}
      />
    </Link>
  );

  return (
    <BottomIconHideWrapper>
      <Modal newModal mobileBottomDrawer>
        <ModalDialog className={styles.modalDialog}>
          <ModalContent className={styles.templateModal}>
            <div className={styles.templateModalContainer}>
              <div className={styles.templateModalContentBlock}>
                <ModalBody className={styles.modalBody}>
                  {renderShopHeader()}
                  {renderShopContent()}
                </ModalBody>

                <ModalFooter className={styles.modalFooter}>
                  <div className={styles.modalFooterCtaHolder}>
                    <Button className={styles.templateModalButton} onClick={handleUseTemplateClick}>
                      {ctaText}
                    </Button>
                  </div>
                  <div className={styles.modalFooterCtaHolder}>
                    <LinkButton
                      href={getPreviewTemplateRoute(feUrl)}
                      className={styles.templateModalButton}
                      styleType="primary-outline"
                    >
                      LIVE PREVIEW
                    </LinkButton>
                  </div>
                </ModalFooter>
              </div>

              <div className={styles.templateModalImageBlock}>
                <CloseButton
                  onClick={() => closeTemplateInfoModal()}
                  className={styles.templateModalClose}
                />
                {renderShopHeader()}
                <div className={styles.templateModalImageHolder}>{renderShopImage()}</div>
              </div>
            </div>
          </ModalContent>
        </ModalDialog>
      </Modal>
    </BottomIconHideWrapper>
  );
};

export default TemplateInfoModal;
